import { Injectable } from "@angular/core";
import { AlertController, ToastController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { Big } from "big.js";
import { IDatePlace } from "src/app/models/location.model";
import { OrderModel } from "src/app/models/order.model";
import { IPayment } from "src/app/models/payment.model";
import { ProductModel } from "src/app/models/product.model";
import { ProfileModel } from "src/app/models/profile.model";
import { TourneeModel } from "src/app/models/tournee.model";
import { DataService } from "../data/data.service";
import { DateService } from "../date/date.service";
import { MathService } from "../math/math.service";

@Injectable({
  providedIn: "root"
})



export class OrderService {

  canStartTourneeStatuses = ["open", "full", "distributing", "prepared"];
  notDeliveredOrderStatuses = ["created", "acknowledged", "accepted", "prepared"];

  statusRank = ['created','accepted','prepared','delivered','notDelivered','rejected','cancelled'];

  statuses = {
    created: {name:'created',status:false, icon:'list-circle-outline', color:'primary', 
      label:'created', rank:1},
    accepted: {name:'accepted',status:false, icon:'checkmark-done-outline', color:'secondary', 
      label:'accepted', rank:2, previous:'created'},
    prepared: {name:'prepared',status:false, icon:'cube-outline', color:'secondary', 
      label:'prepared', rank:3, previous:'accepted'},
    delivered: {name:'delivered',status:false,icon:'bag-handle-outline', color:'secondary', 
      label:'delivered', rank:4, previous:'prepared'},
    notDelivered: {name:'notDelivered',status:false,icon:'close-outline', color:'secondary', 
      label:'notDelivered', rank:5, previous:'prepared'},
    rejected: {name:'rejected',status:false,icon:'close-outline', color:'light', 
      label:'rejected', rank:6,previous:'created'},
    cancelled: {name:'cancelled',status:false,icon:'close-outline', color:'light', 
      label:'cancelled', rank:7}
  }

  statusesArray = Object.values(this.statuses);

  newItem = <OrderModel>{
    details: {
      currency: "EUR",
      format: "solo",
      dateOrdered: Date(),
      type: null
    },
    status: "new",
    producer: {},
    consumer: {},
    route: {
      geocoding: {}
    },
    tournee: null,
    tournees: null,
    products: {
      freeText: "",
      references: {}
    },
    messages: {},
    finances: {
      paymentMode: {},
      productCount: 0,
      totals: {
        sold: 0,
        paid: 0
      }
    }
  };

  constructor(private dataService:DataService, private toastCtrl:ToastController, private mathService:MathService,
    private translateService:TranslateService, private alertCtrl:AlertController, private dateService:DateService) {
    }

  public initOrder(tournee:TourneeModel, consumer?:ProfileModel):OrderModel{
    const order:OrderModel = {
      details: {
        currency: "EUR",
        format: "solo",
        dateOrdered: Date(),
        type: null
      },
      status: "new",
      producer: {},
      consumer: {},
      route: {
        geocoding: {}
      },
      tournee: null,
      tournees: null,
      products: {
        freeText: "",
        references: {}
      },
      messages: {},
      finances: {
        paymentMode: {},
        productCount: 0,
        totals: {
          sold: 0,
          paid: 0
        }
      }
    };
    order.consumer = consumer || {key:'new', details:{
      fullDetails:{}
    }};
    order.producer = {
      key: tournee.producer.key,
      details: tournee.producer.details
    };
    order.details.datePlace = tournee.details.datesPlaces[0];
    order.details.format = "solo";
    order.details.type = tournee.details.type;
    order.tournee = {
      key: tournee.key,
      producer:{
        key:tournee.producer.key
      },
      details: tournee.details
    };
    return order;
  }
    
  async markOrderPrepared(order: OrderModel): Promise<void> {
    console.log(order);
    const oldStatus = order.status;
    let message = "";
    const inputs = [];
    if (order.finances.totals.amountStatus == "estimated" || order.details.productMode == "free") {
      message = this.translateService.instant("tournee.AmountMessage");
      inputs.push({
        type: "number",
        name: "amount",
        label: this.translateService.instant("tournee.AmountFinal"),
        value: order.finances.totals.sold,
        placeholder: this.translateService.instant("tournee.AmountPlaceholder")
      });
    }
    if(order.tournee.details.deliveryType =='vendingMachine' || order.tournee.details.deliveryType =='locker'){
      inputs.push({
        type: "text",
        name: "secret",
        placeholder: this.translateService.instant("order.Secret"),
        label: this.translateService.instant("order.Secret"),
      });
    }
    inputs.push({
      type: "textarea",
      name: "message",
      placeholder: this.translateService.instant("tournee.ClientMessage")
    });
    const alert = await this.alertCtrl.create({
      header: this.translateService.instant("tournee.OrderReady?"),
      cssClass: "alertMsg",
      message: message,
      backdropDismiss: false,
      inputs: inputs,
      buttons: [
        { role: "cancel", text: this.translateService.instant("buttons.Cancel") },
        { role: "ok", text: this.translateService.instant("buttons.Validate") }
      ]
    });
    await alert.present();
    const alertResult = await alert.onDidDismiss();
    if (alertResult.role === "ok") {
      if ((alertResult.data.values.message && alertResult.data.values.message !== "") || (alertResult.data.values.secret && alertResult.data.values.secret !== "")) {
        order.details.commentPrepared = alertResult.data.values.message || null;
        order.details.secret = alertResult.data.values.secret || null;
        await this.dataService.updateOrderPreparedCommentByProducer(order, order.tournee.producer);
      }
      if (order.finances.totals.amountStatus == "estimated" && alertResult.data.values.amount > 0) {
        order.finances = {
          totals: {
            amountStatus: "setByProducer",
            sold: parseFloat(alertResult.data.values.amount)
          }
        };
        await this.dataService.updateOrderTotalSoldByProducer(order, order.tournee.producer);
      }
      await this.changeOrderStatus(order, order.tournee.producer, "prepared");
      return Promise.resolve();
    }
  }

  async markOrderRejected(order: OrderModel, tournee:TourneeModel): Promise<void> {
    const message = this.translateService.instant("tournee.RejectOrderReasonMessage");
    const inputs = [];
    inputs.push({
      type: "textarea",
      name: "message",
      placeholder: this.translateService.instant("tournee.RejectOrderReasonMessagePlaceholder")
    });
    const alert = await this.alertCtrl.create({
      header: this.translateService.instant("tournee.RejectOrder"),
      cssClass: "alertMsg",
      subHeader: message,
      backdropDismiss: false,
      inputs: inputs,
      buttons: [
        { role: "cancel", text: this.translateService.instant("buttons.Cancel") },
        { role: "ok", text: this.translateService.instant("buttons.Reject") }
      ]
    });
    await alert.present();
    const alertResult = await alert.onDidDismiss();
    if (alertResult.role === "ok") {
      if (alertResult.data.values.message && alertResult.data.values.message !== "") {
        // const chat:any = {
        //   status:'rejected',
        //   uid:this.dataService.user.uid,
        //   author: this.dataService.profile.details.shortDetails.name,
        //   message:alertResult.data.values.message,
        //   date:new Date().toISOString(),
        // }
        // this.dataService.createMessage(chat,tournee.key,order.key);
        order.details.commentRejected = alertResult.data.values.message;
        this.dataService.updateOrderRejectedCommentByProducer(order, tournee.producer);
      }
      return this.changeOrderStatus(order, tournee.producer, "rejected");
      //tournee.finances = this.mathService.calculateTourneeRevenue(tournee);
    }
    else{
      return new Promise((resolve, reject) => resolve());
    }
  }

  public markOrderDelivered(order: OrderModel, tournee:TourneeModel): Promise<void> {
    return this.changeOrderStatus(order, tournee.producer, "delivered");
  }

  public markOrderAccepted(order: OrderModel,tournee:TourneeModel): Promise<void> {
    return this.changeOrderStatus(order, tournee.producer, "accepted");
  }
  public markOrderNotDelivered(order: OrderModel,tournee:TourneeModel): Promise<void> {
    return this.changeOrderStatus(order, tournee.producer, "notDelivered");
  }

  public markProductAccepted(order: OrderModel, product: ProductModel): Promise<void> {
    return this.changeProductStatus(order, product, "accepted");
  }

  public markProductPrepared(order: OrderModel, product: ProductModel): Promise<void> {
    return this.changeProductStatus(order, product, "prepared");
  }

  public markProductRejected(order: OrderModel, product: ProductModel): Promise<void> {
    return this.changeProductStatus(order, product, "rejected");
  }

  public markProductDelivered(order: OrderModel, product: ProductModel): Promise<void> {
    return this.changeProductStatus(order, product, "delivered");
  }

  async presentToast(message: string): Promise<void> {
    const toast = await this.toastCtrl.create({
      message,
      duration: 15000,
      buttons: [
        {
          side: "end",
          icon: "close-circle-outline",
          role: "cancel",
          handler: () => {
            console.log("dismiss toast");
            // toast.dismiss();
          }
        }
      ],
      position: "bottom"
    });
    toast.present();
  }

  getDatePlace(order:OrderModel): IDatePlace{
    let datePlace:IDatePlace;
    if(order.details.datePlace){
      datePlace = order.details.datePlace
    }
    else if(order.details.date){
      datePlace = order.details.date;
      datePlace.geocoding = order.tournee?order.tournee.details.geocoding: order.market.details.geocoding
    }
    else{
      datePlace = {
        fromTimeHour:order.tournee?order.tournee.details.fromTimeHour: order.market.details.fromTimeHour,
        toTimeHour:order.tournee?order.tournee.details.toTimeHour: order.market.details.toTimeHour,
        geocoding:order.tournee?order.tournee.details.geocoding: order.market.details.geocoding,
        date:order.tournee?order.tournee.details.date: order.market.details.date,
      }
    }
    return datePlace
  }

  convertOrderToDatePlaceSystem(order:OrderModel): OrderModel{
    order.details.datePlace = this.getDatePlace(order);
    return order;
  }

  changeOrderStatus(order: OrderModel, producer: ProfileModel, status: string): Promise<void> {
    order.status = status;
    return this.dataService.updateOrderStatusByProducer(order, producer);
  }

  async changeProductStatus(order: OrderModel, product: ProductModel, status: string): Promise<void> {
    product.status = status;
    // this.dataService.updateProductOrderStatus(order,product);
  }

  sortAndTransformToArray(tournee: TourneeModel): OrderModel[] {
    if(tournee.orders){
      return Object.entries(tournee.orders).map(entry => <OrderModel>{ 
          key: entry[0], 
          producer: tournee.producer,
          ...entry[1] as {},
          tournee: { key: tournee.key, details:tournee.details,producer:tournee.producer }, 
      })      
    }
    else return [];
  }
  getDistinctStatuses(orders:OrderModel[]):string[]{
    return [...new Set(orders.map((item) => this.statuses[item.status]))].sort((a,b) => a.rank-b.rank);
  }

  getDistinctDates(orders:OrderModel[]):string[]{
    return [...new Set(orders.map((item) => item.details.date))];
  }

  filterOrdersByDateAndStatus(tournee:TourneeModel,date:IDatePlace,status:any){
    // console.log('filterOrdersByDateAndStatus', date, status, tournee);

    if(date){
      date.geocoding = date.geocoding || tournee.details.geocoding; //retro compatibility
    }

    const orderTable = this.enrichAndTransformToArray(tournee).filter(item => 
    {
      if(item.details && item.details.datePlace && date){

        item.details.datePlace.geocoding = item.details.datePlace.geocoding || tournee.details.geocoding;//retro compatibility

        const orderDatePlaceKey = this.dateService.generateDatePlaceKey(item.details.datePlace);
        const datePlaceKey = this.dateService.generateDatePlaceKey(date);
        if(status && item.status){
          return orderDatePlaceKey == datePlaceKey && item.status == status.name;
        }
        else{
          return orderDatePlaceKey == datePlaceKey;
        }
      }
      else if(status && item.status){
        return item.status == status.name
      }
      else{
        return true;
      }
    }).sort((a,b) => {
      if(!a.consumer || a.consumer.key == 'anonymous' || !a.consumer.details.fullDetails || !a.consumer.details.fullDetails.lastname){
        return -1
      }
      else if(!b.consumer || b.consumer.key == 'anonymous' || !b.consumer.details.fullDetails || !b.consumer.details.fullDetails.lastname){
        return 1
      }
      else if(a.consumer.details.fullDetails.lastname && b.consumer.details.fullDetails.lastname){
        return (a.consumer.details.fullDetails.lastname.toLowerCase() < b.consumer.details.fullDetails.lastname.toLowerCase())?-1:1;
      }
      else {
        return 0
      }
    })  
    // console.log(orderTable);
    let tourneesByType = {};
    for(let currentValue of orderTable){
      if(!tourneesByType[currentValue.status]){
        tourneesByType[currentValue.status] = {
          name:currentValue.status,
          array:[],
          finances:this.mathService.getEmptyFinances()
        }
      }
      currentValue = this.mathService.calculateOrderRevenue(currentValue);
      tourneesByType[currentValue.status].array.push(currentValue);
      const finances = this.mathService.addFinances(this.mathService.financesFloatToBig(tourneesByType[currentValue.status].finances),currentValue.finances);
      tourneesByType[currentValue.status].finances = this.mathService.financesBigToFloat(finances);
    };
    const ordersOrganized = {
      all: orderTable,
      byStatus: Object.values(tourneesByType).sort(this.sortByStatus)
    }
    return ordersOrganized;
  }

  sortByStatus(a,b) {
    const statusRank = ['created','accepted','prepared','delivered','notDelivered','rejected','cancelled'];
    return statusRank.indexOf(a.name) - statusRank.indexOf(b.name);
  }
  
  enrichAndTransformToArray(tournee: TourneeModel): OrderModel[] {
    if(tournee.orders){
      return Object.entries(tournee.orders).map(entry => <OrderModel>{ key: entry[0], 
          // producer: tournee.producer,
          ...entry[1] as {},
          tournee: { key: tournee.key, details:tournee.details,producer:tournee.producer }})      
    }
    else return [];
  }
  

  sortAndTransformToArrayByType(tournee: TourneeModel, date?:any): any {
    let tourneesByType = {};
    const mathService = this.mathService;
    if (tournee.orders !== undefined) {
      const reducer = function (accumulator, currentValue) {
        if (accumulator[currentValue.status]) {
          accumulator[currentValue.status].array.push(currentValue);
        } else {
          accumulator[currentValue.status] = {
            name:currentValue.status,
            array:[currentValue]}
        }
        accumulator[currentValue.status].finances = mathService.calculateTourneeRevenue2(<TourneeModel>{
          orders: accumulator[currentValue.status].array, details:tournee.details 
        },true).finances;
        return accumulator;
      };
      tourneesByType = Object.entries(tournee.orders)
        .map(item => <OrderModel>{ key: item[0], tournee: { key: tournee.key, details:tournee.details, producer:tournee.producer}, producer:tournee.producer, ...item[1] as {} })
        .filter(item => {
          if(item.details.date && date){
            if(tournee.details.deliveryFormat == 'tournee'){
              return item.details.date.moment == date.moment && item.details.date.date == date.date
            }
            else{
              return item.details.date.fromTimeHour == date.fromTimeHour && item.details.date.date == date.date
            }
          }
          
          else { return true}
        })
        .sort((a,b) => {
          if(!a.consumer || a.consumer.key == 'anonymous' || !a.consumer.details.fullDetails || !a.consumer.details.fullDetails.lastname){
            return -1
          }
          else if(!b.consumer || b.consumer.key == 'anonymous' || !b.consumer.details.fullDetails || !b.consumer.details.fullDetails.lastname){
            return 1
          }
          else if(a.consumer.details.fullDetails.lastname && b.consumer.details.fullDetails.lastname){
            return (a.consumer.details.fullDetails.lastname.toLowerCase() < b.consumer.details.fullDetails.lastname.toLowerCase())?-1:1;
          }
          else {
            return 0
          }
        })        
        .reduce(reducer, {});
    }
    return Object.values(tourneesByType).sort(function (a: any, b: any) {
      const statusRank = ['created','accepted','prepared','delivered','rejected'];
      return statusRank.indexOf(a.name) - statusRank.indexOf(b.name);
        });
  }

  updatePaymentStatus(order:OrderModel){
    const payments = Object.entries(order.payments);
    let paidBig = new Big(0);
    for(const [key,value] of payments){
      const payment:IPayment = value;
      paidBig = paidBig.plus(new Big(payment.debitedFunds.amount));
    }
    order.finances.totals.paid = this.mathService.bigToFloat(paidBig);
    if(order.finances.totals.paid == order.finances.totals.sold){
      order.paymentStatus = 'cashedIn';
    }
    return order;
  }

}
